import { useTranslation } from 'react-i18next';
import React from 'react';
import RankBadge from '../elo/RankBadge';
import CountryFlag from '../common/CountryFlag';

/**
 * Player selection step for creating a new match.
 * Shows a grid of available players with online status and ELO.
 */
export default function StepPlayerSelection({
  players, selectedPlayers, onlineIds, busyMap = {}, matchType,
  onTogglePlayer, onMatchTypeChange, onCreateMatch, loading,
}) {
  const { t } = useTranslation();
  const matchTypes = ['BO1', 'BO3', 'BO5'];

  return (
    <div className="step-content">
      <div className="step-header">
        <h2>{t('createMatch.selectPlayers', { count: selectedPlayers.length })}</h2>
      </div>

      <div className="match-type-pills" role="radiogroup" aria-label={t('createMatch.matchType')}>
        {matchTypes.map(mt => (
          <button
            key={mt}
            type="button"
            role="radio"
            aria-checked={matchType === mt}
            className={`match-type-pill ${matchType === mt ? 'active' : ''}`}
            onClick={() => onMatchTypeChange(mt)}
          >
            <span className="match-type-pill-label">{mt}</span>
            <span className="match-type-pill-meta">
              {mt === 'BO1' ? t('createMatch.oneMap') : mt === 'BO3' ? t('createMatch.bestOf3') : t('createMatch.bestOf5')}
            </span>
          </button>
        ))}
      </div>

      <div className="player-grid">
        {[...players].sort((a, b) => {
          // Busy players sink to the bottom; otherwise online-first.
          const aBusy = busyMap[String(a.discord_id)] ? 1 : 0;
          const bBusy = busyMap[String(b.discord_id)] ? 1 : 0;
          if (aBusy !== bBusy) return aBusy - bBusy;
          const aOnline = onlineIds.has(a.discord_id) ? 1 : 0;
          const bOnline = onlineIds.has(b.discord_id) ? 1 : 0;
          return bOnline - aOnline;
        }).map(player => {
          const isSelected = selectedPlayers.some(p => p.discord_id === player.discord_id);
          const isOnline = onlineIds.has(player.discord_id);
          const busyMatchId = busyMap[String(player.discord_id)] || null;
          const isBusy = !!busyMatchId;
          return (
            <div
              key={player.discord_id}
              className={`player-card ${isSelected ? 'selected' : ''} ${isBusy ? 'busy' : ''}`}
              onClick={() => { if (!isBusy) onTogglePlayer(player); }}
              title={isBusy ? `Redan i match #${busyMatchId}` : undefined}
            >
              <div className="player-card-avatar-wrapper">
                {player.avatar && <img src={player.avatar} alt="" className="player-card-avatar" />}
                {isOnline && !isBusy && <span className="online-indicator" title={t('sidebar.online')} />}
                {isBusy && <span className="busy-indicator" title={t('createMatch.inMatchNo', { id: busyMatchId })}>\ud83c\udfae</span>}
              </div>
              <div className="player-card-info">
                <span className="player-card-name">
                  {player.country && <CountryFlag code={player.country} size="sm" className="player-card-flag" />}
                  {player.steam_username || player.discord_username}
                </span>
                {isBusy ? (
                  <span className="player-card-busy-label">I match #{busyMatchId}</span>
                ) : player.elo && (
                  <span className="player-card-elo">
                    <RankBadge rank={player.elo.rank} size="small" />
                    {player.elo.elo} ELO
                  </span>
                )}
              </div>
              <div className={`player-card-check ${isSelected ? 'checked' : ''}`}>
                {isSelected ? '\u2713' : ''}
              </div>
            </div>
          );
        })}
      </div>

      <button
        className="next-btn"
        disabled={selectedPlayers.length < 1 || loading}
        onClick={onCreateMatch}
      >
        {loading ? t('createMatch.creating') : t('createMatch.createWithCount', { count: selectedPlayers.length })}
      </button>
    </div>
  );
}
